import { useMemo } from 'react';

import { useMutation } from '@tanstack/react-query';

import AsyncModal from 'components/modals/AsyncModal';
import ConfirmEscalationModal from 'components/modals/ConfirmEscalationModal';
import ActivityInput from 'components/shared/activityInput/ActivityInput';
import useActivityInput from 'components/shared/activityInput/useActivityInput';
import { useProfile } from 'context/profile';
import useInvalidateQueries from 'hooks/useInvalidateQueries';
import Activity, { ActivityType } from 'models/Activity';
import Episode, { EpisodeOptions } from 'models/Episode';
import Escalation from 'models/Escalation';
import { EscalationTypes } from 'models/Escalation';
import { ManagerGroupTypeConfig } from 'models/GroupType';
import LocationEpisode from 'models/LocationEpisode';
import { Note } from 'models/Notes';
import Review from 'models/Review';
import { createActivity } from 'services/api/activity';
import { TaggableUserProps } from 'services/api/locationEpisode/taggableUsers';
import { useToastActions } from 'stores/toastStore';

type Props = {
  episode?: Episode;
  locationEpisode?: LocationEpisode;
  taggableUserProps?: TaggableUserProps;
  newNoteProps?: Partial<Note>;
  mutationFn?: (params: any) => Promise<Activity>;
  review?: Review;
  canElevate?: boolean;
};

export default function NewActivity({
  episode,
  locationEpisode,
  taggableUserProps,
  newNoteProps,
  mutationFn = createActivity,
  review,
  canElevate = true,
}: Props) {
  const invalidateQueries = useInvalidateQueries();
  const { addToast } = useToastActions();
  const { profile } = useProfile();

  const { mutateAsync, isPending: creatingActivity } = useMutation({
    mutationFn: mutationFn,
  });

  const escalationType = useMemo(() => {
    return new Escalation({ episode: episode as Partial<EpisodeOptions> }).typeForProfile(profile);
  }, [episode, profile]);

  const showEscalate = useMemo(() => {
    if (!locationEpisode) return false;

    return !!(
      escalationType === EscalationTypes.Escalation &&
      ((locationEpisode.owner.groupType.config as ManagerGroupTypeConfig)?.canReceiveEscalations ?? true)
    );
  }, [escalationType, locationEpisode]);

  const { note, setNote, resetNote, getSerializedNoteValues } = useActivityInput({ escalationType });

  const handleSubmit = async () => {
    if (note.escalated && escalationType === EscalationTypes.Escalation) {
      const confirmed = await new AsyncModal(ConfirmEscalationModal).render();
      if (!confirmed) return;
    }

    try {
      await mutateAsync({
        ...getSerializedNoteValues(),
        type: ActivityType.NOTE,
        locationEpisodeId: locationEpisode?.id,
        reviewId: review?.id,
        ...newNoteProps,
      });

      invalidateQueries({
        activities: true,
        attachmentsCount: true,
        authorization: { id: locationEpisode?.activeAuthorizationReview?.id },
        locationEpisode: { id: locationEpisode?.id },
      });
      resetNote();
    } catch {
      addToast({ text: 'Something went wrong. Please try again.' });
    }
  };

  return (
    <ActivityInput
      data-cy='activity-input'
      escalationType={escalationType}
      taggable
      attachable
      taggableUserProps={taggableUserProps}
      showEscalate={canElevate && showEscalate}
      showPriority={canElevate && escalationType === EscalationTypes.PriorityNote}
      showPostButton
      submitting={creatingActivity}
      locationEpisodeId={locationEpisode?.id}
      onSubmit={handleSubmit}
      values={note}
      setValues={setNote}
      darkBorder={false}
      heightSettings={{ minHeight: '140px' }}
    />
  );
}
